import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppSelectField } from '@/form/FormUI';
import type { NodeFormProps } from '@/nodes';
import type { EntryNodeType } from '@/nodes/types';
import { useSavedFormValues } from '@/state';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { EntryDataSchema, type EntryDataType } from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof EntryDataSchema>;

export const EntryForm: FC<NodeFormProps<EntryNodeType>> = ({ flowId, node, updateNode, cancelUpdate }) => {
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, EntryDataType>({
        resolver: zodResolver(EntryDataSchema),
        // NOT seeded with an explicit 'space'. An absent `intro` is what the backfill migration
        // looks for, so materializing the default here would make merely opening this panel and
        // hitting Save opt a guide out of its own backfill — invisibly, since the form and the node
        // badge both render 'space' either way.
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: EntryDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <div className="text-gray-700">This is the entrypoint to the guide</div>

                    <AppSelectField
                        control={form.control}
                        name="intro"
                        label="Intro Screen"
                        placeholder="Space intro (default)"
                        options={[
                            { label: "Space intro (the space's own paragraphs)", value: 'space' },
                            { label: 'Generic intro copy', value: 'generic' },
                            { label: 'Skip — go straight to the first step', value: 'skip' },
                        ]}
                        description={
                            'Skipping also removes the "Skip Guided Setup" button, which this screen is the only ' +
                            'place to offer. Equipment guides and sub-guides always skip, whatever this says.'
                        }
                    />
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};
